import React, { useState } from "react";
import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
    Alert,
    useTheme,
    Stack,
    IconButton,
    InputAdornment
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import API from "../../api/axiosInstance";

const ChangePassword: React.FC = () => {
    const theme = useTheme();
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [showPassword, setShowPassword] = useState(false);

    const validateRequirements = {
        length: newPassword.length >= 8,
        upper: /[A-Z]/.test(newPassword),
        lower: /[a-z]/.test(newPassword),
        number: /[0-9]/.test(newPassword),
        special: /[\W_]/.test(newPassword),
    };

    const allValid = Object.values(validateRequirements).every(Boolean);

    const handleChangePassword = async () => {
        setError("");
        setMessage("");

        if (!currentPassword || !newPassword || !confirmPassword) {
            setError("Please fill in all fields.");
            return;
        }

        if (newPassword !== confirmPassword) {
            setError("New password and confirm password do not match.");
            return;
        }

        if (!allValid) {
            setError("Password does not meet all requirements.");
            return;
        }

        try {
            const user = JSON.parse(localStorage.getItem('user') || "{}");
            const headers = {
                headers: {
                    authToken: user.user?.authtoken,
                    sessionId: user.user?.sessionId,
                },
            };

            await API.post(
                "/auth/change-password",
                { "CurrentPassword": currentPassword, "NewPassword" : newPassword },
                headers
            );

            setMessage("✅ Password updated successfully.");
            setCurrentPassword("");
            setNewPassword("");
            setConfirmPassword("");
        } catch (error) {
            const errMsg = (error as any)?.response?.data || "Failed to update password.";
            setError("Error: " + errMsg);
        }
    };

    const renderCheck = (valid: boolean) =>
        valid ? (
            <CheckCircleIcon fontSize="small" color="success" />
        ) : (
            <CancelIcon fontSize="small" color="error" />
        );

    const passwordProps = {
        type: showPassword ? "text" : "password",
        InputProps: {
            endAdornment: (
                <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                </InputAdornment>
            )
        }
    };

    return (
        <Box
            sx={{
                backgroundColor: theme.palette.background.default,
                color: theme.palette.text.primary,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "80vh",
                p: 3
            }}
        >
            <Paper
                elevation={3}
                sx={{
                    p: 4,
                    width: "100%",
                    maxWidth: 500,
                    borderRadius: 3,
                    backgroundColor: theme.palette.background.paper
                }}
            >
                <Typography variant="h5" fontWeight={600} gutterBottom>
                    Change Password
                </Typography>

                <Stack spacing={2} mt={2}>
                    <TextField
                        label="Current Password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        fullWidth
                        {...passwordProps}
                    />
                    <TextField
                        label="New Password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        fullWidth
                        {...passwordProps}
                    />

                    <Box sx={{ pl: 1 }}>
                        <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 500 }}>
                            Password must contain:
                        </Typography>
                        <Stack spacing={0.5} sx={{ pl: 2 }} mt={1}>
                            <Typography variant="body2">{renderCheck(validateRequirements.length)} At least 8 characters</Typography>
                            <Typography variant="body2">{renderCheck(validateRequirements.upper)} At least 1 uppercase letter</Typography>
                            <Typography variant="body2">{renderCheck(validateRequirements.lower)} At least 1 lowercase letter</Typography>
                            <Typography variant="body2">{renderCheck(validateRequirements.number)} At least 1 number</Typography>
                            <Typography variant="body2">{renderCheck(validateRequirements.special)} At least 1 special character</Typography>
                        </Stack>
                    </Box>

                    <TextField
                        label="Confirm New Password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        fullWidth
                        {...passwordProps}
                    />

                    {error && <Alert severity="error">{error}</Alert>}
                    {message && <Alert severity="success">{message}</Alert>}

                    <Button
                        variant="contained"
                        onClick={handleChangePassword}
                        sx={{ mt: 2 }}
                    >
                        Update Password
                    </Button>
                </Stack>
            </Paper>
        </Box>
    );
};

export default ChangePassword;
